/* eslint-disable camelcase */ // API payload shape
import { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useBlocker } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { isSeatEditable } from './memberTableUtils'
import { SelectableSeatType } from './SeatTypeDropdown'

import { ApiError } from '@/api'
import { changeCostCenterSeats } from '@/api/costCenters'
import { useToast } from '@/components/Toast/ToastContext'
import { COST_CENTERS_QUERY_KEY } from '@/pages/app/Settings/CostCenters'
import { AppDispatch } from '@/store'
import { fetchAllUsersThunk } from '@/store/features/member/memberSlice'
import { Member } from '@/types'
import { isPublicPath } from '@/utils/publicPaths'

const GENERIC_ERROR = "Couldn't save the seat changes. Try again."

type SeatChange = { user_id: string; seat_type: SelectableSeatType }

// A save can charge the customer, so each one keeps its request ids and bodies; a retry resends them unchanged.
type Intent = { costCenterId: string; requestId: string; changes: SeatChange[] }

/** Stage seat changes in the members table and save them together, one request per cost center. */
export const useSeatChangeDraft = (members: Member[], enterpriseId: string, editableCostCenterIds: Set<string>) => {
  const dispatch = useDispatch<AppDispatch>()
  const queryClient = useQueryClient()
  const { updateToast } = useToast()

  const [staged, setStaged] = useState<Record<string, SelectableSeatType>>({})
  const [unconfirmed, setUnconfirmed] = useState(false)
  const intents = useRef<Intent[] | null>(null)

  const pending = members.filter(
    (member) =>
      isSeatEditable(member, editableCostCenterIds) &&
      staged[member.userId] &&
      staged[member.userId] !== member.seatType
  )
  const pendingIds = pending.map((member) => member.userId).join()

  // A refresh that shows a staged seat applied, or a member no longer editable, drops it from the draft.
  useEffect(() => {
    const kept = new Set(pendingIds.split(','))

    setStaged((current) => {
      const next = Object.fromEntries(Object.entries(current).filter(([userId]) => kept.has(userId)))
      return Object.keys(next).length === Object.keys(current).length ? current : next
    })

    if (!pendingIds) {
      setUnconfirmed(false)
      intents.current = null
    }
  }, [pendingIds])

  const isUnresolved = unconfirmed && pending.length > 0
  const hasUnsaved = pending.length > 0

  useEffect(() => {
    if (!hasUnsaved) return

    const warn = (event: BeforeUnloadEvent) => event.preventDefault()
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [hasUnsaved])

  // A sign-out navigates to /login before this re-renders with the cleared draft, so it is never blocked.
  const leaveBlocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      hasUnsaved && currentLocation.pathname !== nextLocation.pathname && !isPublicPath(nextLocation.pathname)
  )

  const refresh = async () => {
    void queryClient.invalidateQueries({ queryKey: COST_CENTERS_QUERY_KEY(enterpriseId) })

    try {
      await dispatch(fetchAllUsersThunk()).unwrap()
    } catch {
      updateToast("Couldn't refresh your team.", 'error')
    }
  }

  const clear = () => {
    setStaged({})
    setUnconfirmed(false)
    intents.current = null
  }

  const fail = (error: ApiError) => {
    if (error.status === 503 || error.isNetworkError) {
      setUnconfirmed(true)
      void refresh()
      return
    }

    // The request wasn't processed, so the staged changes stay for another try.
    if (error.status === 429) {
      updateToast('Too many seat changes at once. Wait a minute and try again.', 'error')
      void refresh()
      return
    }

    const costCenterIds = intents.current?.map((intent) => intent.costCenterId)
    intents.current = null
    setUnconfirmed(false)

    if (error.code === 'request_id_reused') {
      Sentry.captureMessage('seat change reused a request id', { level: 'error', extra: { costCenterIds } })
      updateToast(GENERIC_ERROR, 'error')
    } else if (error.status === 409) {
      updateToast(error.detail || GENERIC_ERROR, 'error')
      void refresh()
    } else if (error.status === 403 || error.status === 404) {
      setStaged({})
      updateToast("These seats can't be changed right now.", 'error')
      void refresh()
    } else {
      updateToast(GENERIC_ERROR, 'error')
    }
  }

  const mutation = useMutation({
    mutationFn: async (targets: Intent[]) => {
      // A retry after a partial success resends every intent; the server treats a repeated one as already done.
      for (const target of targets) {
        await changeCostCenterSeats(target.costCenterId, { request_id: target.requestId, changes: target.changes })
      }
    },
    onSuccess: async () => {
      clear()
      updateToast('Seat changes saved', 'success')

      await refresh()
    },
    onError: (error) => fail(error as unknown as ApiError),
  })

  const isLocked = mutation.isPending || isUnresolved

  const stage = (member: Member, seatType: SelectableSeatType) => {
    if (isLocked) return

    setStaged((current) => {
      const next = { ...current }

      if (seatType === member.seatType) {
        delete next[member.userId]
      } else {
        next[member.userId] = seatType
      }

      return next
    })
  }

  const buildIntents = (): Intent[] => {
    const byCostCenter = new Map<string, SeatChange[]>()

    for (const member of pending) {
      const costCenterId = member.costCenterId as string
      const changes = byCostCenter.get(costCenterId) ?? []
      changes.push({ user_id: member.userId, seat_type: staged[member.userId] })
      byCostCenter.set(costCenterId, changes)
    }

    return Array.from(byCostCenter, ([costCenterId, changes]) => ({
      costCenterId,
      requestId: crypto.randomUUID(),
      changes,
    }))
  }

  const save = () => {
    if (mutation.isPending || pending.length === 0) return

    if (!isUnresolved || !intents.current) intents.current = buildIntents()

    mutation.mutate(intents.current)
  }

  const discard = () => {
    if (isLocked) return

    clear()
  }

  return {
    staged,
    pendingCount: pending.length,
    isSaving: mutation.isPending,
    isUnresolved,
    isLocked,
    leaveBlocker,
    stage,
    save,
    discard,
  }
}
